import { host } from '../script/variables';

// Owner-facing plan & subscription API (partner JWT in Authorization header)
const request = async (path, { method = 'GET', body } = {}) => {
  const token = localStorage.getItem('partnertoken');
  const res = await fetch(`${host}${path}`, {
    method,
    headers: {
      Authorization: `${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = {};
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) {
    const err = new Error(data.msg || data.error || (res.status === 429 ? 'Too many requests. Please wait a moment.' : `Request failed (${res.status})`));
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

export const partnerPlanApi = {
  catalog:        ()     => request('/partner/plans/catalog'),
  me:             ()     => request('/partner/plans/me'),
  history:        ()     => request('/partner/plans/history'),
  receipt:        (id)   => request(`/partner/plans/payments/${encodeURIComponent(id)}`),
  quote:          (body) => request('/partner/plans/quote', { method: 'POST', body }),
  startTrial:     (body) => request('/partner/plans/trial', { method: 'POST', body }),
  downgrade:      (body) => request('/partner/plans/downgrade', { method: 'POST', body }),
  cancelScheduled: ()    => request('/partner/plans/scheduled-change', { method: 'DELETE' }),
  cancel:         (body) => request('/partner/plans/cancel', { method: 'POST', body }),
  resume:         ()     => request('/partner/plans/resume', { method: 'POST' }),
  checkout:       (body) => request('/payment/subscription/checkout', { method: 'POST', body }),
  verify:         (orderId) => request('/payment/subscription/verify', { method: 'POST', body: { orderId } }),
};

// ─── Formatting ─────────────────────────────────────────────────────────────
export const money = (minor, currency = 'INR', { decimals } = {}) => {
  if (minor === null || minor === undefined) return '—';
  const digits = decimals ?? (minor % 100 ? 2 : 0);
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(minor / 100);
};

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

export const daysUntil = (d) => (d ? Math.ceil((new Date(d).getTime() - Date.now()) / 86400000) : null);

export const INTERVALS = {
  month:     { label: 'Monthly',     short: 'mo',      per: 'month',    months: 1 },
  quarter:   { label: 'Quarterly',   short: 'qtr',     per: 'quarter',  months: 3 },
  half_year: { label: 'Half-yearly', short: '6 mo',    per: '6 months', months: 6 },
  year:      { label: 'Yearly',      short: 'yr',      per: 'year',     months: 12 },
};

export const STATUS = {
  active:          { label: 'Active',          tone: 'good' },
  trialing:        { label: 'Free trial',      tone: 'info' },
  pending_payment: { label: 'Payment due',     tone: 'warn' },
  past_due:        { label: 'Past due',        tone: 'warn' },
  grace_period:    { label: 'Grace period',    tone: 'warn' },
  suspended:       { label: 'Suspended',       tone: 'bad'  },
  cancelled:       { label: 'Cancelled',       tone: 'muted' },
  expired:         { label: 'Expired',         tone: 'muted' },
};

export const PAYMENT_STATUS = {
  created:         { label: 'Awaiting payment', tone: 'warn' },
  superseded:      { label: 'Replaced',         tone: 'muted' },
  paid:            { label: 'Processing',       tone: 'info' },
  fulfilling:      { label: 'Processing',       tone: 'info' },
  fulfilled:       { label: 'Paid',             tone: 'good' },
  refund_required: { label: 'Under review',     tone: 'warn' },
  refunded:        { label: 'Refunded',         tone: 'muted' },
  expired:         { label: 'Expired',          tone: 'muted' },
  cancelled:       { label: 'Cancelled',        tone: 'muted' },
  failed:          { label: 'Failed',           tone: 'bad' },
};

export const KIND_LABEL = { new: 'New subscription', upgrade: 'Upgrade', renewal: 'Renewal', downgrade: 'Downgrade' };

export const formatLimitValue = (item) => {
  if (item.unlimited) return 'Unlimited';
  if (item.unit === 'bytes') {
    const gb = item.value / (1024 ** 3);
    return gb >= 1 ? `${+gb.toFixed(1)} GB` : `${Math.round(item.value / (1024 ** 2))} MB`;
  }
  return Number(item.value || 0).toLocaleString('en-IN');
};
